import React from 'react';
import { X, Check, Edit2, Loader2, Maximize2, MessageSquare, Copy, CheckCheck, AlertTriangle } from 'lucide-react';
import { useLlmAgent } from './LlmAgentContext';
import { parseChangePreview, extractProseWithoutTool, parseToolInvocation } from './previewParser';
import './SuggestionBar.css';

interface Props {
  /** Called when the user clicks Edit — receives suggestion text for editing */
  onEdit?: (content: string) => void;
}

export function SuggestionBar({ onEdit }: Props) {
  const { lastSuggestion, isLoading, streamingContent, webLlmLoading, lastError, dismiss, accept, sessionMessages, setIsConvoOpen } = useLlmAgent();
  const [copied, setCopied] = React.useState(false);
  // This must run before every conditional return. An AI response changes
  // `lastSuggestion` from null to a value, so placing this hook below the
  // loading/empty branches made React see a different hook count on completion.
  const preview = React.useMemo(() => {
    if (!lastSuggestion || lastSuggestion.kind !== 'action') return null;
    return parseChangePreview(lastSuggestion.content, lastSuggestion.appId);
  }, [lastSuggestion]);

  const displayProse = React.useMemo(() => {
    if (!lastSuggestion) return '';
    const content = typeof lastSuggestion.content === 'string'
      ? lastSuggestion.content
      : JSON.stringify(lastSuggestion.content, null, 2);
    if (preview?.actionType === 'tool' || parseToolInvocation(content)) {
      return extractProseWithoutTool(content);
    }
    return content;
  }, [lastSuggestion, preview]);

  const handleCopy = () => {
    if (!lastSuggestion) return;
    navigator.clipboard.writeText(lastSuggestion.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  // Show loading state while the LLM is processing
  if (isLoading) {
    const isInitializing = webLlmLoading !== null;
    const downloadPct = Math.round((webLlmLoading?.downloadProgress ?? 0) * 100);
    const loadPct = Math.round((webLlmLoading?.loadProgress ?? 0) * 100);
    return (
      <div className="suggestion-bar suggestion-bar--loading" aria-live="polite" aria-busy="true">
        <div className="suggestion-bar__header">
          <span className="suggestion-bar__label">{lastSuggestion?.kind === 'action' ? 'Prepared action' : 'AI response'}</span>
          {sessionMessages.length > 0 && (
            <button
              className="suggestion-bar__history-btn"
              onClick={() => setIsConvoOpen(true)}
              aria-label="View session conversation"
              title="View session conversation"
            >
              <MessageSquare size={13} />
              <span>{sessionMessages.length}</span>
            </button>
          )}
        </div>
        <div className="suggestion-bar__loading">
          <Loader2 size={14} className="suggestion-bar__spinner" aria-hidden="true" />
          <span>{isInitializing ? 'Preparing local AI…' : (streamingContent ? 'Generating…' : 'Generating suggestion…')}</span>
        </div>
        {webLlmLoading && (
          <div className="suggestion-bar__model-progress">
            <div className="suggestion-bar__progress-row">
              <span>Download</span>
              <div className="suggestion-bar__progress-track" role="progressbar" aria-label="Local AI download progress" aria-valuenow={downloadPct} aria-valuemin={0} aria-valuemax={100}>
                <span style={{ width: `${downloadPct}%` }} />
              </div>
              <strong>{downloadPct}%</strong>
            </div>
            <div className="suggestion-bar__progress-row">
              <span>Load</span>
              <div className="suggestion-bar__progress-track" role="progressbar" aria-label="Local AI load progress" aria-valuenow={loadPct} aria-valuemin={0} aria-valuemax={100}>
                <span style={{ width: `${loadPct}%` }} />
              </div>
              <strong>{loadPct}%</strong>
            </div>
            <p className="suggestion-bar__model-status">{webLlmLoading.text || 'Initialising local model…'}</p>
          </div>
        )}
        {streamingContent && <div className="suggestion-bar__streaming">{streamingContent}</div>}
      </div>
    );
  }

  // Show error state if the LLM failed
  if (lastError && !lastSuggestion) {
    const errorMessages: Record<string, string> = {
      'auth-error': 'AI provider authentication failed — check your API key in Settings → AI.',
      'network-error': 'Could not reach the AI provider — check your internet connection.',
      'webllm-unavailable': 'Local AI model is not available in this environment.',
      'provider-error': 'The AI provider returned an error. Try again shortly.',
      'local-only': 'This feature uses content other people wrote and runs only on on-device AI.',
    };
    const msg = errorMessages[lastError.code] ?? 'AI suggestion failed. Try again.';

    return (
      <div className="suggestion-bar suggestion-bar--error" role="alert">
        <div className="suggestion-bar__header">
          <span className="suggestion-bar__label suggestion-bar__label--error">AI suggestion failed</span>
          <div className="suggestion-bar__header-actions">
            {sessionMessages.length > 0 && (
              <button
                className="suggestion-bar__history-btn"
                onClick={() => setIsConvoOpen(true)}
                aria-label="View session conversation"
                title="View session conversation"
              >
                <MessageSquare size={13} />
                <span>{sessionMessages.length}</span>
              </button>
            )}
            <button className="suggestion-bar__close" onClick={dismiss} aria-label="Dismiss error">
              <X size={14} />
            </button>
          </div>
        </div>
        <p className="suggestion-bar__error-text">{msg}</p>
      </div>
    );
  }

  if (!lastSuggestion) {
    return null;
  }

  return (
    <div className="suggestion-bar">
      <div className="suggestion-bar__header">
        <div className="suggestion-bar__title-group">
          <span className="suggestion-bar__label">{lastSuggestion?.kind === 'action' ? 'Prepared action' : 'AI response'}</span>
          {lastSuggestion.prompt && (
            <span className="suggestion-bar__prompt-hint" title={lastSuggestion.prompt}>
              for &ldquo;{lastSuggestion.prompt.length > 30 ? `${lastSuggestion.prompt.slice(0, 30)}…` : lastSuggestion.prompt}&rdquo;
            </span>
          )}
        </div>
        <div className="suggestion-bar__header-actions">
          <button
            className="suggestion-bar__icon-btn"
            onClick={() => setIsConvoOpen(true)}
            aria-label="Expand conversation history"
            title="Expand session conversation"
          >
            <Maximize2 size={13} />
            {sessionMessages.length > 0 && <span className="suggestion-bar__count-badge">{sessionMessages.length}</span>}
          </button>
          <button
            className="suggestion-bar__icon-btn"
            onClick={handleCopy}
            aria-label="Copy answer"
            title="Copy answer"
          >
            {copied ? <CheckCheck size={13} color="var(--success, #10b981)" /> : <Copy size={13} />}
          </button>
          <button className="suggestion-bar__close" onClick={dismiss} aria-label="Dismiss suggestion">
            <X size={14} />
          </button>
        </div>
      </div>

      {preview && (
        <div className="suggestion-bar__preview">
          <div className="suggestion-bar__preview-header">
            <span className="suggestion-bar__preview-title">{String(preview.title ?? '')}</span>
            <span className={`suggestion-bar__preview-badge ${preview.destructiveNotice ? 'suggestion-bar__preview-badge--destructive' : ''}`}>
              {String(preview.actionType ?? '')}
            </span>
          </div>
          {preview.items.length > 0 && (
            <ul className="suggestion-bar__preview-items">
              {preview.items.map((item, idx) => (
                <li key={idx}>
                  <strong>{String(item.label)}:</strong> {typeof item.detail === 'object' ? JSON.stringify(item.detail) : String(item.detail ?? '')}
                </li>
              ))}
            </ul>
          )}
          {preview.destructiveNotice && (
            <div className="suggestion-bar__preview-notice">
              <AlertTriangle size={12} />
              <span>{String(preview.destructiveNotice)}</span>
            </div>
          )}
        </div>
      )}

      {displayProse.trim().length > 0 && (
        <div className="suggestion-bar__content">
          {displayProse}
        </div>
      )}
      <div className="suggestion-bar__actions">
        {lastSuggestion.kind === 'action' && (
          <button
            className="suggestion-bar__btn suggestion-bar__btn--primary"
            onClick={() => { void accept(lastSuggestion.content, lastSuggestion.id).catch(() => {}); }}
          >
            <Check size={14} /> Confirm &amp; execute
          </button>
        )}
        {onEdit && displayProse.trim().length > 0 && (
          <button
            className="suggestion-bar__btn"
            onClick={() => {
              onEdit(displayProse);
              dismiss();
            }}
          >
            <Edit2 size={14} /> Edit
          </button>
        )}
        <button
          className="suggestion-bar__btn"
          onClick={() => setIsConvoOpen(true)}
        >
          <MessageSquare size={14} /> Expand Convo
        </button>
        <button className="suggestion-bar__btn" onClick={dismiss}>
          {preview?.actionType === 'tool' ? 'Cancel' : 'Dismiss'}
        </button>
      </div>
    </div>
  );
}
